import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, Lock, Eye, Database, Users } from 'lucide-react';

const Datenschutzerklaerung = () => {
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect to German version if not already on it, but only once
  React.useEffect(() => {
    // Check if we're already on the German datenschutzerklaerung path
    if (location.pathname === '/de/datenschutzerklaerung') {
      return; // Already on the correct path, no need to redirect
    }
    
    // Only redirect if we're not on German and not already redirecting
    if (currentLanguage !== 'de' && !location.pathname.includes('/de/datenschutzerklaerung')) {
      navigate('/de/datenschutzerklaerung', { replace: true });
    }
  }, [currentLanguage, navigate, location.pathname]);

  // Don't render anything while redirecting
  if (currentLanguage !== 'de' && location.pathname !== '/de/datenschutzerklaerung') {
    return null;
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="container-custom py-16">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <Shield className="h-12 w-12 text-primary-600" />
            </div>
            <h1 className="text-4xl font-bold text-neutral-900 mb-4">
              Datenschutzerklärung
            </h1>
            <p className="text-lg text-neutral-600">
              Stand: 15. Juli 2025
            </p>
          </div>

          {/* Company Info */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
              Verantwortlicher
            </h2>
            <div className="space-y-2 text-neutral-700">
              <p><strong>surf & game GmbH</strong></p>
              <p>Amtsgericht Charlottenburg (Berlin) HRB 109460 B</p>
              <p>Hasenheide 115</p>
              <p>D-10967 Berlin</p>
              <p>Deutschland</p>
              <p className="mt-4">
                <strong>E-Mail:</strong> info@co2neutralpage.com
              </p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="space-y-8">
            {/* 1. Allgemeine Informationen */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4 flex items-center">
                <Lock className="h-6 w-6 text-primary-600 mr-3" />
                1. Allgemeine Informationen
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Der Schutz Ihrer persönlichen Daten ist uns ein wichtiges Anliegen. 
                  Diese Datenschutzerklärung informiert Sie über Art, Umfang und Zweck 
                  der Verarbeitung personenbezogener Daten auf unserer Website.
                </p>
                <p>
                  Personenbezogene Daten sind alle Informationen, die sich auf eine 
                  identifizierte oder identifizierbare natürliche Person beziehen.
                </p>
              </div>
            </div>

            {/* 2. Erhebung und Verarbeitung von Daten */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4 flex items-center">
                <Database className="h-6 w-6 text-primary-600 mr-3" />
                2. Erhebung und Verarbeitung von Daten
              </h2>
              <div className="prose prose-neutral max-w-none">
                <h3 className="text-xl font-semibold mb-3">2.1 Automatisch erfasste Daten</h3>
                <p>
                  Bei Ihrem Besuch auf unserer Website werden automatisch folgende 
                  Informationen erfasst:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>IP-Adresse</li>
                  <li>Browsertyp und -version</li>
                  <li>Betriebssystem</li>
                  <li>Zugriffsdatum und -zeit</li>
                  <li>Besuchte Seiten</li>
                  <li>Referrer-URL</li>
                </ul>

                <h3 className="text-xl font-semibold mb-3 mt-6">2.2 Kontaktformular</h3>
                <p>
                  Bei Nutzung unseres Kontaktformulars werden folgende Daten erhoben:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Name und Vorname</li>
                  <li>E-Mail-Adresse</li>
                  <li>Telefonnummer (optional)</li>
                  <li>Firmenname (optional)</li>
                  <li>Nachrichteninhalt</li>
                </ul>
              </div>
            </div>

            {/* 3. Zweck der Datenverarbeitung */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4 flex items-center">
                <Eye className="h-6 w-6 text-primary-600 mr-3" />
                3. Zweck der Datenverarbeitung
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>Wir verarbeiten Ihre Daten für folgende Zwecke:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Bereitstellung und Optimierung unserer Website</li>
                  <li>Beantwortung Ihrer Anfragen</li>
                  <li>Durchführung von CO₂-Neutralitäts-Zertifizierungen</li>
                  <li>Erfüllung gesetzlicher Verpflichtungen</li>
                  <li>Verbesserung unserer Dienstleistungen</li>
                </ul>
              </div>
            </div>

            {/* 4. Rechtsgrundlagen */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                4. Rechtsgrundlagen der Datenverarbeitung
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>Die Verarbeitung Ihrer Daten erfolgt auf folgenden Rechtsgrundlagen:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Art. 6 Abs. 1 lit. a DSGVO:</strong> Einwilligung</li>
                  <li><strong>Art. 6 Abs. 1 lit. b DSGVO:</strong> Vertragserfüllung</li>
                  <li><strong>Art. 6 Abs. 1 lit. f DSGVO:</strong> Berechtigte Interessen</li>
                  <li><strong>Art. 6 Abs. 1 lit. c DSGVO:</strong> Rechtliche Verpflichtung</li>
                </ul>
              </div>
            </div>

            {/* 5. Datenweitergabe */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                5. Datenweitergabe an Dritte
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Eine Weitergabe Ihrer Daten an Dritte erfolgt nur in folgenden Fällen:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Bei Ihrer ausdrücklichen Einwilligung</li>
                  <li>Zur Erfüllung gesetzlicher Verpflichtungen</li>
                  <li>An Dienstleister im Rahmen der Auftragsverarbeitung</li>
                  <li>Bei berechtigtem Interesse (z.B. zur Abwehr von Rechtsansprüchen)</li>
                </ul>
              </div>
            </div>

            {/* 6. Ihre Rechte */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4 flex items-center">
                <Users className="h-6 w-6 text-primary-600 mr-3" />
                6. Ihre Rechte
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>Sie haben folgende Rechte bezüglich Ihrer personenbezogenen Daten:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Auskunftsrecht:</strong> Sie können Auskunft über Ihre gespeicherten Daten verlangen</li>
                  <li><strong>Berichtigungsrecht:</strong> Sie können die Berichtigung falscher Daten verlangen</li>
                  <li><strong>Löschungsrecht:</strong> Sie können die Löschung Ihrer Daten verlangen</li>
                  <li><strong>Einschränkungsrecht:</strong> Sie können die Einschränkung der Verarbeitung verlangen</li>
                  <li><strong>Widerspruchsrecht:</strong> Sie können der Verarbeitung widersprechen</li>
                  <li><strong>Datenübertragbarkeit:</strong> Sie können Ihre Daten in einem strukturierten Format erhalten</li>
                </ul>
              </div>
            </div>

            {/* 7. Cookies */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                7. Verwendung von Cookies
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Unsere Website verwendet Cookies. Cookies sind kleine Textdateien, 
                  die auf Ihrem Endgerät gespeichert werden.
                </p>
                <h3 className="text-xl font-semibold mb-3 mt-4">Arten von Cookies:</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Notwendige Cookies:</strong> Für die Grundfunktionen der Website</li>
                  <li><strong>Funktionale Cookies:</strong> Für erweiterte Funktionen</li>
                  <li><strong>Analyse-Cookies:</strong> Zur Verbesserung der Website</li>
                </ul>
              </div>
            </div>

            {/* 8. Sicherheit */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                8. Datensicherheit
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Wir setzen technische und organisatorische Sicherheitsmaßnahmen ein, 
                  um Ihre Daten gegen Manipulation, Verlust, Zerstörung oder gegen den 
                  Zugriff unberechtigter Personen zu schützen.
                </p>
              </div>
            </div>

            {/* 9. Änderungen */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                9. Änderungen dieser Datenschutzerklärung
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Wir behalten uns vor, diese Datenschutzerklärung anzupassen, 
                  damit sie stets den aktuellen rechtlichen Anforderungen entspricht 
                  oder um Änderungen unserer Leistungen in der Datenschutzerklärung 
                  umzusetzen.
                </p>
              </div>
            </div>

            {/* 10. Kontakt */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                10. Kontakt
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Bei Fragen zur Erhebung, Verarbeitung oder Nutzung Ihrer 
                  personenbezogenen Daten wenden Sie sich bitte an:
                </p>
                <div className="bg-neutral-50 p-4 rounded-lg mt-4">
                  <p><strong>surf & game GmbH</strong></p>
                  <p>Hasenheide 115</p>
                  <p>D-10967 Berlin</p>
                  <p>Deutschland</p>
                  <p className="mt-2">
                    <strong>E-Mail:</strong> info@co2neutralpage.com
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Datenschutzerklaerung; 